/*
 * Custom waveform icons for the two continuous-playback overlap behaviors.
 * Both glyphs read as the same left-to-right sentence — "what happens to the
 * current segment, then what happens to the overlapping one" — so learning
 * either icon teaches the other:
 *
 *   IconOverlapSkip — segment plays (solid bars), overlapping segment is NOT
 *                     played (ghosted bars under a prohibition circle).
 *   IconOverlapChop — current segment is trimmed (mdi scissors, reusing the
 *                     exact Material Design content-cut path for family
 *                     consistency), then the next segment plays (solid bars).
 *
 * Rendered as inline SVG (24x24 viewBox, fill/stroke: currentColor) so they
 * inherit the surrounding icon color. No separate light/dark versions are
 * needed: currentColor follows the Vuetify theme exactly like the mdi font.
 *
 *   import { IconOverlapSkip, IconOverlapChop } from "./icons/overlapIcons";
 *   components: { IconOverlapSkip, IconOverlapChop },
 *   <icon-overlap-skip/>  /  <icon-overlap-chop :size="20"/>
 */

// Path data from Material Design Icons "content-cut" (Apache 2.0), scaled and
// positioned inside the composed glyph below.
var MDI_CONTENT_CUT_PATH = "M9.64 7.64c.23-.5.36-1.05.36-1.64 0-2.21-1.79-4-4-4S2 3.79 2 6s1.79 4 4 4c.59 0 1.14-.13 1.64-.36L10 12l-2.36 2.36C7.14 14.13 6.59 14 6 14c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4c0-.59-.13-1.14-.36-1.64L12 14l7 7h3v-1L9.64 7.64zM6 8c-1.1 0-2-.89-2-2s.9-2 2-2 2 .89 2 2-.9 2-2 2zm0 12c-1.1 0-2-.89-2-2s.9-2 2-2 2 .89 2 2-.9 2-2 2zm6-7.5c-.28 0-.5-.22-.5-.5s.22-.5.5-.5.5.22.5.5-.22.5-.5.5zM19 3l-6 6 2 2 7-7V3h-3z";

function bar(h, x, y, height, opacity) {
    return h("rect", { attrs: { x: x, y: y, width: 2, height: height, rx: 1, opacity: opacity || 1 } });
}

function svgWrap(h, ctx, children) {
    var data = ctx.data;
    return h(
        "svg",
        {
            class: data.class,
            staticClass: data.staticClass,
            style: [{ verticalAlign: "middle" }, data.style, data.staticStyle],
            attrs: Object.assign({}, data.attrs, {
                width: ctx.props.size,
                height: ctx.props.size,
                viewBox: "0 0 24 24",
                fill: "currentColor",
                "aria-hidden": "true",
            }),
            on: data.on,
        },
        children
    );
}

export const IconOverlapSkip = {
    name: "IconOverlapSkip",
    functional: true,
    props: { size: { type: [Number, String], default: 24 } },
    render: function (h, ctx) {
        return svgWrap(h, ctx, [
            // the segment that plays
            bar(h, 2, 9, 6),
            bar(h, 5, 6, 12),
            bar(h, 8, 4.5, 15),
            // the overlapping segment, ghosted — it will not be played
            bar(h, 13, 8, 8, 0.3),
            bar(h, 16, 7, 10, 0.3),
            bar(h, 19, 9, 6, 0.3),
            // prohibition circle over the ghosted segment
            h("circle", {
                attrs: { cx: 16.5, cy: 12, r: 5.2, fill: "none", stroke: "currentColor", "stroke-width": 1.8 },
            }),
            h("line", {
                attrs: { x1: 12.9, y1: 8.4, x2: 20.1, y2: 15.6, stroke: "currentColor", "stroke-width": 1.8, "stroke-linecap": "round" },
            }),
        ]);
    },
};

export const IconOverlapChop = {
    name: "IconOverlapChop",
    functional: true,
    props: { size: { type: [Number, String], default: 24 } },
    render: function (h, ctx) {
        return svgWrap(h, ctx, [
            // the current segment being trimmed (mdi scissors, scaled into the left half)
            h("g", { attrs: { transform: "translate(1,5.2) scale(0.55)" } }, [
                h("path", { attrs: { d: MDI_CONTENT_CUT_PATH } }),
            ]),
            // the next segment playing after the chop
            bar(h, 15.5, 6.5, 11),
            bar(h, 18.5, 4.5, 15),
            bar(h, 21.5, 8, 8),
        ]);
    },
};

export default { IconOverlapSkip: IconOverlapSkip, IconOverlapChop: IconOverlapChop };